import { appConfig } from 'Config/app';
import { auth } from 'Utils/auth';

// Constants
import { WebsocketClients, CallbackActions, WsCloseCodes } from 'Config/websocketConstants';

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;

class WebsocketConnectionManager {
    constructor() {
        this.wsConnection = null;
        this.connectedClients = new Set();
        this.connectionStatus = null;
        this.actionToComponentCallbacks = {}; // Action: Set([Callback1, Callback2])
        this.reconnectionTimeOut = null;
        this.retryCnt = 0;
        this.subscription = null; // Last subscribe payload; resent on every (re)connect
    }

    validateMsg = (event) => {
        const msg = JSON.parse(event.data);
        const action = msg?.action;
        const data = msg?.data;
        if (action && data) {
            return [action, data];
        } else {
            return [null, null];
        }
    }

    clearReconnectionTimeout = () => {
        clearTimeout(this.reconnectionTimeOut);
        this.reconnectionTimeOut = null;
    }

    setStatus = (status) => {
        this.connectionStatus = status;
        this.runActionCallbacks(CallbackActions.WS_STATUS_UPDATE, status);
    }

    // Register a client; the socket opens on the first subscription (sendPayload).
    connect = (wsClientName) => {
        if (wsClientName && wsClientName in WebsocketClients) {
            this.connectedClients.add(WebsocketClients[wsClientName]);
        }
    }

    open = () => {
        this.clearReconnectionTimeout();
        const ws = new WebSocket(appConfig.WS_BASE_URL + '/v1/real-time/');
        this.wsConnection = ws;
        this.setStatus(ws.readyState);

        ws.onopen = () => {
            this.setStatus(ws.readyState);
            this.retryCnt = 0;
            if (this.subscription) {
                this.send(this.subscription);
            }
        }

        ws.onmessage = (event) => { // event {object} It should contain action (action type to execute the callback) and data.
            const [action, data] = this.validateMsg(event);
            if (action !== null && data !== null) {
                this.runActionCallbacks(action, data)
            }
        }

        ws.onclose = (event) => {
            if (this.wsConnection !== ws) { // Superseded by a newer socket
                return;
            }
            this.wsConnection = null;
            this.setStatus(WebSocket.CLOSED);
            this.handleClose(event.code);
        }
    }

    handleClose = (code) => {
        if (this.connectedClients.size === 0) {
            return;
        }
        switch (code) {
            case WsCloseCodes.UNAUTHENTICATED:
                auth.logout();
                window.location.replace('/login');
                return;
            case WsCloseCodes.REVOKED:
            case WsCloseCodes.BAD_REQUEST:
                console.error(`Realtime socket closed with ${code}; not reconnecting.`);
                return;
            case WsCloseCodes.SUBSCRIBE_TIMEOUT:
                return; // Reopens on the next sendPayload
            default: { // Lifetime limit, network drop, server restart
                const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.retryCnt, RECONNECT_MAX_MS);
                this.retryCnt += 1;
                this.reconnectionTimeOut = setTimeout(this.open, delay);
            }
        }
    }

    close = () => {
        this.clearReconnectionTimeout();
        this.subscription = null;
        this.retryCnt = 0;
        const ws = this.wsConnection;
        this.wsConnection = null;
        if (ws) {
            ws.close();
            this.setStatus(WebSocket.CLOSED);
        }
    }

    disconnect = (wsClientName) => {
        if (wsClientName && wsClientName in WebsocketClients) {
            this.connectedClients.delete(WebsocketClients[wsClientName]);
        }
        if (this.connectedClients.size === 0) {
            this.close();
        }
    }

    send = (payload) => {
        // Send credentials in the frame, never in URLs or channel names.
        this.wsConnection.send(JSON.stringify({ ...payload, token: auth.getToken() }));
    }

    /**
     * Subscribes over the WebSocket, opening it if needed. The payload is kept
     * and resent after every reconnect.
     *
     * @param {object} payload - The paload to send.
     * @param {string} payload.action - The action type
     * @param {object} payload.data - The action payload.
     */
    sendPayload = (payload) => {
        this.subscription = payload;
        if (!this.wsConnection) {
            this.retryCnt = 0;
            this.open();
        } else if (this.wsConnection.readyState === WebSocket.OPEN) {
            this.send(payload);
        } // CONNECTING: onopen sends it
    }

    // Add the client callback so it can be trigger by specifc action
    addActionCallback = (action, clientCallback) => {
        if (action in this.actionToComponentCallbacks) {
            this.actionToComponentCallbacks[action].add(clientCallback);
        } else {
            this.actionToComponentCallbacks[action] = new Set([clientCallback]);
        }
    }

    deleteActionCallback = (action, clientCallback) => {
        if (action in this.actionToComponentCallbacks) {
            this.actionToComponentCallbacks[action].delete(clientCallback);
        }
    }

    runActionCallbacks = (action, data) => {
        if (action in this.actionToComponentCallbacks) {
            for (const callback of this.actionToComponentCallbacks[action]) {
                callback(data);
            }
        }
    }
}

const centralWsConnectionManager = new WebsocketConnectionManager();
export default centralWsConnectionManager;
